import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Undo2 } from 'lucide-react';
import type { CleanupAction } from './control-panel-dummy-data';

const TYPE_BADGE_COLOR: Record<CleanupAction['type'], { bg: string; color: string }> = {
  cleanup: { bg: '#b2f5ea', color: '#234e52' },
  optimization: { bg: '#e9d8fd', color: '#44337a' },
  review: { bg: '#feebc8', color: '#7b341e' },
};

const TYPE_LABEL: Record<CleanupAction['type'], string> = {
  cleanup: 'Cleanup',
  optimization: 'Optimization',
  review: 'Manual Task',
};

interface ActionsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  actions: CleanupAction[];
  onRemoveAction: (id: string) => void;
  onUndo?: () => void;
  canUndo?: boolean;
}

export const ActionsDetailModal: React.FC<ActionsDetailModalProps> = ({
  isOpen,
  onClose,
  actions,
  onRemoveAction,
  onUndo,
  canUndo = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '680px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f7fafc',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#1a202c',
            }}
          >
            Selected Actions ({actions.length})
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#718096',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '16px 20px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {actions.length === 0 ? (
            <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
              No actions selected.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {actions.map((action, index) => (
                <React.Fragment key={action.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '10px 0',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '4px',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#2d3748',
                          }}
                        >
                          {action.name}
                        </span>
                        <span style={{
                          display: 'inline-block',
                          padding: '0 4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          borderRadius: '2px',
                          flexShrink: 0,
                          backgroundColor: TYPE_BADGE_COLOR[action.type].bg,
                          color: TYPE_BADGE_COLOR[action.type].color,
                        }}>
                          {TYPE_LABEL[action.type]}
                        </span>
                        <span style={{
                          display: 'inline-block',
                          padding: '0 4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          borderRadius: '2px',
                          flexShrink: 0,
                          border: '1px solid #805ad5',
                          color: '#805ad5',
                          backgroundColor: 'transparent',
                        }}>
                          {action.category}
                        </span>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {action.description}
                      </p>
                    </div>
                    <button
                      aria-label={`Remove action ${action.name}`}
                      onClick={() => onRemoveAction(action.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#fc8181',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        flexShrink: 0,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fff5f5')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {index < actions.length - 1 && (
                    <hr style={{ margin: 0, border: 'none', borderTop: '1px solid #e2e8f0' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {onUndo ? (
            <button
              onClick={onUndo}
              disabled={!canUndo}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                fontWeight: 500,
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                backgroundColor: canUndo ? '#ffffff' : '#f7fafc',
                color: canUndo ? '#4a5568' : '#a0aec0',
                cursor: canUndo ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (canUndo) e.currentTarget.style.backgroundColor = '#f7fafc';
              }}
              onMouseLeave={(e) => {
                if (canUndo) e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              <Undo2 size={13} />
              Undo
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={onClose}
            style={{
              fontSize: '14px',
              fontWeight: 500,
              padding: '6px 16px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              color: '#4a5568',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
